import { useMemo } from 'react';
import {
  Box,
  Typography,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import BoltIcon from '@mui/icons-material/Bolt';

export const GPTSelector = ({
  selectedGptId,
  onSelectGPT,
  onCloseMobile,
  customGpts = [],
  apiUrl
}) => {
  // Separate default GPT from other GPTs
  const { defaultGpt, otherGpts } = useMemo(() => {
    const defaultGpt = customGpts.find(gpt => gpt.is_default);
    const otherGpts = customGpts.filter(gpt => !gpt.is_default);
    return { defaultGpt, otherGpts };
  }, [customGpts]);

  const handleSelectGPT = (gptId) => {
    onSelectGPT(gptId);
    // Close mobile sidebar after selection
    if (window.innerWidth <= 768 && onCloseMobile) {
      onCloseMobile();
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: 1.5,
        pt: 1.5,
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider',
        maxHeight: '40%', // Limit GPT section to 40% of sidebar
        minHeight: 100, // Ensure at least some space for GPTs
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pb: 1.5, flexShrink: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Assistants
        </Typography>
        <Link
          href="/manage-assistants.html"
          underline="none"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.primary',
            '&:hover': { color: 'text.secondary' },
          }}
        >
          <EditIcon sx={{ fontSize: 12 }} />
          <span>Manage</span>
        </Link>
      </Box>

      {/* GPT List - scrollable */}
      <List
        disablePadding
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: 1,
          minHeight: 0,
          '& .MuiListItemButton-root': { borderRadius: 1, mb: 0.25 },
        }}
      >
        {/* Default GPT (company's General GPT or fallback) */}
        {defaultGpt ? (
          <ListItemButton
            selected={selectedGptId === defaultGpt.id || selectedGptId === '' || !selectedGptId}
            onClick={() => handleSelectGPT(defaultGpt.id)}
            sx={{
              py: 0.75,
              px: 1,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {defaultGpt.favicon_url ? (
                <Avatar
                  src={`${apiUrl}${defaultGpt.favicon_url}?t=${Date.now()}`}
                  alt={defaultGpt.name}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                  }}
                >
                  <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'white' }} />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary={defaultGpt.name}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                noWrap: true,
              }}
            />
          </ListItemButton>
        ) : (
          // Fallback for companies without a default GPT (pre-migration)
          <ListItemButton
            selected={selectedGptId === '' || !selectedGptId}
            onClick={() => handleSelectGPT('')}
            sx={{
              py: 0.75,
              px: 1,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)',
                }}
              >
                <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'white' }} />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary="General"
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                noWrap: true,
              }}
            />
          </ListItemButton>
        )}

        {/* Custom GPTs (excluding the default) */}
        {otherGpts.map((gpt) => (
          <ListItemButton
            key={gpt.id}
            selected={selectedGptId === gpt.id}
            onClick={() => handleSelectGPT(gpt.id)}
            sx={{
              py: 0.75,
              px: 1,
              '&.Mui-selected': {
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.selected' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {gpt.favicon_url ? (
                <Avatar
                  src={`${apiUrl}${gpt.favicon_url}?t=${Date.now()}`}
                  alt={gpt.name}
                  sx={{ width: 24, height: 24 }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    background: 'linear-gradient(135deg, #9333ea 0%, #ec4899 100%)',
                  }}
                >
                  <BoltIcon sx={{ fontSize: 14, color: 'white' }} />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary={gpt.name}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 500,
                noWrap: true,
              }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};
